import type { SyntheticEvent } from "react";
import Typography from "@mui/material/Typography";
import { getFormInputProps } from "util/forms";
import { selectCurrencies } from "features/general/selectors";
import Autocomplete from "ui/atoms/Autocomplete";
import { TextFieldLoadable } from "ui/atoms/TextField";
import { InputableRadioGroup, OnboardingInputControl } from "ui/molecules/Input";
import StatefulSelector from "ui/widgets/StatefulSelector";
import OnboardingForm from "../../OnboardingForm";
import { FormField, type OnboardingFinancialInformationProps } from "./types";

const FinancialInformation = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingFinancialInformationProps) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values);
            setTimeout(() => setFieldTouched(formField, true, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
            >
                What is your preferred currency?
            </Typography>
            <OnboardingInputControl
                name={FormField.PreferredCurrency}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <StatefulSelector
                        fullWidth
                        disableClearable
                        disabled={disabled}
                        layout={null}
                        selectOptions={selectCurrencies}
                        value={values[FormField.PreferredCurrency]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.PreferredCurrency, form),
                            onBlur: handleBlur,
                            required: true,
                            fullWidth: true,
                            name: FormField.PreferredCurrency,
                            label: 'Preferred Currency'
                        }}
                        onChange={onAutocompleteChange(FormField.PreferredCurrency)}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h5'
            >
                Forecasted Information
            </Typography>
            <Typography
                variant='h6'
            >
                Monthly Basis
            </Typography>
            <OnboardingInputControl
                name={FormField.AvgTransactionNoMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.AvgTransactionNoMonth, form)}
                        fullWidth
                        required
                        modifiers={{
                            between: [0, 1_000_000_000]
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.AvgTransactionNoMonth}
                        value={values[FormField.AvgTransactionNoMonth]}
                        label='Average Number of Transactions'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.MaxTransactionVolumeMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <Autocomplete
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        loading={isBootstrapDataLoading}
                        size='small'
                        options={bootstrapData[FormField.MaxTransactionVolumeMonth]}
                        value={values[FormField.MaxTransactionVolumeMonth]}
                        onChange={onAutocompleteChange(FormField.MaxTransactionVolumeMonth)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.MaxTransactionVolumeMonth, form),
                            required: true,
                            name: FormField.MaxTransactionVolumeMonth,
                            label: 'Maximum Transactions Volume'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.MaxTransactionNoCustomerMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.MaxTransactionNoCustomerMonth, form)}
                        fullWidth
                        required
                        modifiers={{
                            between: [1, 1_000_000]
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.MaxTransactionNoCustomerMonth}
                        value={values[FormField.MaxTransactionNoCustomerMonth]}
                        label='Maximum Number of Transactions per customer'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.EstimatedPercentChargebacksRefundsMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.EstimatedPercentChargebacksRefundsMonth, form)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: '%'
                        }}
                        modifiers={{
                            decimal: 2,
                            between: [0, 100]
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.EstimatedPercentChargebacksRefundsMonth}
                        value={values[FormField.EstimatedPercentChargebacksRefundsMonth]}
                        label='Estimated Number of Chargebacks / Refunds (%)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Current Month
            </Typography>
            <OnboardingInputControl
                name={FormField.TransactionNoCurrentMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.TransactionNoCurrentMonth, form)}
                        fullWidth
                        required
                        modifiers={{
                            between: [0, 1_000_000_000]
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.TransactionNoCurrentMonth}
                        value={values[FormField.TransactionNoCurrentMonth]}
                        label='Number of transactions this month'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Ticket Amount
            </Typography>
            <OnboardingInputControl
                name={FormField.AvgTransactionAmount}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.AvgTransactionAmount, form)}
                        fullWidth
                        required
                        modifiers={{
                            decimal: 2,
                            between: [0, Number.MAX_SAFE_INTEGER]
                        }}
                        InputProps={{
                            endAdornment: (
                                <Typography
                                    variant='overline'
                                    color='primary.main'
                                >
                                    {values[FormField.PreferredCurrency]}
                                </Typography>
                            )
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.AvgTransactionAmount}
                        value={values[FormField.AvgTransactionAmount]}
                        label='Average Ticket Amount (if applicable)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.MaxTransactionAmount}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.MaxTransactionAmount, form)}
                        fullWidth
                        required
                        modifiers={{
                            decimal: 2
                        }}
                        InputProps={{
                            endAdornment: (
                                <Typography
                                    variant='overline'
                                    color='primary.main'
                                >
                                    {values[FormField.PreferredCurrency]}
                                </Typography>
                            )
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.MaxTransactionAmount}
                        value={values[FormField.MaxTransactionAmount]}
                        label='Maximum Ticket Amount (if applicable)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Projected Sales Volume
            </Typography>
            <OnboardingInputControl
                name={FormField.SalesVolumeCurrentMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <Autocomplete
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        loading={isBootstrapDataLoading}
                        size='small'
                        options={bootstrapData[FormField.SalesVolumeCurrentMonth]}
                        value={values[FormField.SalesVolumeCurrentMonth]}
                        onChange={onAutocompleteChange(FormField.SalesVolumeCurrentMonth)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.SalesVolumeCurrentMonth, form),
                            required: true,
                            name: FormField.SalesVolumeCurrentMonth,
                            label: 'Current month sales volume'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.ExpectedSalesVolumeMonth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <Autocomplete
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        loading={isBootstrapDataLoading}
                        size='small'
                        options={bootstrapData[FormField.ExpectedSalesVolumeMonth]}
                        value={values[FormField.ExpectedSalesVolumeMonth]}
                        onChange={onAutocompleteChange(FormField.ExpectedSalesVolumeMonth)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.ExpectedSalesVolumeMonth, form),
                            required: true,
                            name: FormField.ExpectedSalesVolumeMonth,
                            label: 'Expected sales volume per month'
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h5'
            >
                Processing History
            </Typography>
            <OnboardingInputControl
                name={FormField.IsProcessingHistoryOverSixMonths}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slotProps={{
                            typography: {
                                children: 'Do you have 6 months of processing history?*'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.IsProcessingHistoryOverSixMonths,
                                name: FormField.IsProcessingHistoryOverSixMonths,
                                value: values[FormField.IsProcessingHistoryOverSixMonths],
                                onChange: event => {
                                    handleChange(event);

                                    if (!Number(event.target.value)) {
                                        setFieldValue(FormField.UsedPsps, '');
                                        setTimeout(() => setFieldTouched(FormField.UsedPsps, true, true));
                                    }
                                },
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.UsedPsps, form),
                                required: !!Number(values[FormField.IsProcessingHistoryOverSixMonths]),
                                disabled: disabled || !Number(values[FormField.IsProcessingHistoryOverSixMonths]),
                                name: FormField.UsedPsps,
                                value: values[FormField.UsedPsps],
                                label: 'IF YES, please provide names of Payment Processors used',
                                placeholder: 'Comma separated list of PSPs',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.HasLeftPreviousProcessors}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slotProps={{
                            typography: {
                                children: 'Did you leave a previous processor?*'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.HasLeftPreviousProcessors,
                                name: FormField.HasLeftPreviousProcessors,
                                value: values[FormField.HasLeftPreviousProcessors],
                                onChange: event => {
                                    handleChange(event);

                                    if (!Number(event.target.value)) {
                                        setFieldValue(FormField.LeftPreviousProcessorsInput, '');
                                        setTimeout(() => setFieldTouched(FormField.LeftPreviousProcessorsInput, true, true));
                                    }
                                },
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.LeftPreviousProcessorsInput, form),
                                required: !!Number(values[FormField.HasLeftPreviousProcessors]),
                                disabled: disabled || !Number(values[FormField.HasLeftPreviousProcessors]),
                                name: FormField.LeftPreviousProcessorsInput,
                                value: values[FormField.LeftPreviousProcessorsInput],
                                label: 'IF YES, please provide details and sources',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Product Currencies*
            </Typography>
            <OnboardingInputControl
                name={FormField.SalesCurrencies}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <StatefulSelector
                        fullWidth
                        multiple
                        disableClearable
                        disabled={disabled}
                        layout={null}
                        selectOptions={selectCurrencies}
                        value={values[FormField.SalesCurrencies]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.SalesCurrencies, form),
                            onBlur: handleBlur,
                            fullWidth: true,
                            name: FormField.SalesCurrencies,
                            label: 'Select the currency or currencies in which your product(s) are sold:'
                        }}
                        onChange={onAutocompleteChange(FormField.SalesCurrencies)}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Totals
            </Typography>
            <OnboardingInputControl
                name={FormField.TotalTransactionAmountYearly}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.TotalTransactionAmountYearly, form)}
                        fullWidth
                        required
                        modifiers={{
                            decimal: 2,
                            between: [0, Number.MAX_SAFE_INTEGER]
                        }}
                        InputProps={{
                            endAdornment: (
                                <Typography
                                    variant='overline'
                                    color='primary.main'
                                >
                                    {values[FormField.PreferredCurrency]}
                                </Typography>
                            )
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.TotalTransactionAmountYearly}
                        value={values[FormField.TotalTransactionAmountYearly]}
                        label='Total annual processing volume across all existing methods'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Ratios
            </Typography>
            <OnboardingInputControl
                name={FormField.CurrentPercentChargebacksRefundsRatio}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.CurrentPercentChargebacksRefundsRatio, form)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: '%'
                        }}
                        modifiers={{
                            decimal: 2,
                            between: [0, 100]
                        }}
                        type='number'
                        disabled={disabled}
                        size='small'
                        name={FormField.CurrentPercentChargebacksRefundsRatio}
                        value={values[FormField.CurrentPercentChargebacksRefundsRatio]}
                        label='Current Chargeback / Refunds to Sales Ratio (%)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default FinancialInformation;
